import { unparse } from "papaparse";

import { getListAll } from "../utils/api";
import {
  Translator,
  denormalizeId,
  denormalizeIds,
  translateHeaderRow,
  translateValue,
  formatDateColumns,
  sliceObjectArrayColumn,
  sliceObjectColumn,
  sumJsonFieldValuesByPriceCalculationMode,
  translateGradePenalties,
  translateColumn,
  translateConfirmationTimeframe,
} from "../utils/csv";
import {
  formatPriceCalculationMode,
  getDisplayStoragePeriodBonusValue,
  getDisplayTradingVolumeValue,
  getDisplayUnitPriceValue,
} from "../utils/price_calculation";

type Exporter = (data: any[]) => Promise<string>;

export const getConverter = (resource: string, translate: Translator): Exporter => {
  switch (resource) {
    case "adjustment_deals":
      return getAdjustmentDealConverter(translate);
    case "offer_requests":
      return getOfferRequestConverter(translate);
    case "payments":
      return getPaymentConverter(translate);
    case "spot_contract_payments":
      return getSpotContractPaymentConverter(translate);
    default:
      throw new Error("unknown resource");
  }
};

const PAYMENT_COLUMNS = [
  "amend_id",
  "id",
  "status",
  "producer_company_name",
  "handover_date",
  "payment_due_date",
  "total_bill_in_yen",
  "price_calculation_mode",
  "brown_rice_in_yen",
  "product_payment_adjustments_in_yen",
  "commission_fee_adjustments_in_yen",
  "business_consignment_fee_in_yen",
  "delivery_fee_in_yen",
  "delivery_agency_fee_in_yen",
  "invoiced_at",
];

const getPaymentConverter = (translate: Translator): Exporter => {
  return async (data: any[]) => {
    let converted = data.concat([]);

    converted = converted.map((row) => ({
      ...row,
      amend_id: `A${row.amend_id}`,
    }));
    converted = translateValue(converted, "status", translate, "payment_status");
    converted = sumJsonFieldValuesByPriceCalculationMode(
      converted,
      "brown_rice_in_yen",
      "product_payments",
      "trading_volume_in_yen",
      "trading_volume_excluding_tax_in_yen"
    );
    converted = translateValue(converted, "price_calculation_mode", translate);
    converted = converted.map((row) => ({
      ...row,
      business_consignment_fee_in_yen: row.commission_fee_in_yen,
    }));
    converted = formatDateColumns(converted, ["handover_date", "payment_due_date", "invoiced_at"]);

    converted = translateHeaderRow(converted, "payments", translate, PAYMENT_COLUMNS);
    return unparse(converted);
  };
};

const getSpotContractPaymentConverter = (translate: Translator): Exporter => {
  return async (data: any[]) => {
    let converted = data.concat([]);

    converted = converted.map((row) => ({
      ...row,
      amend_id: `S${row.amend_id}`,
    }));
    converted = translateValue(converted, "status", translate, "payment_status");
    converted = sumJsonFieldValuesByPriceCalculationMode(
      converted,
      "brown_rice_in_yen",
      "product_payments",
      "trading_volume_in_yen",
      "trading_volume_excluding_tax_in_yen"
    );
    converted = translateValue(converted, "price_calculation_mode", translate);
    converted = converted.map((row) => ({
      ...row,
      business_consignment_fee_in_yen: row.commission_fee_in_yen,
    }));
    converted = formatDateColumns(converted, ["handover_date", "payment_due_date", "invoiced_at"]);

    converted = translateHeaderRow(converted, "spot_contract_payments", translate, PAYMENT_COLUMNS);
    return unparse(converted);
  };
};

const ADJUSTMENT_DEAL_COLUMNS = [
  "id",
  "status",
  "confirmation_timeframe",
  "producer.company_name",
  "annum.name",
  "prefecture.name",
  "region.name",
  "brand.name",
  "grade.name",
  "packaging_form.name",
  "trading_units",
  "trading_sacks",
  "price_calculation_mode",
  "unit_price",
  "trading_volume_in_yen",
  "special_conditions",
  "special_conditions_memo",
  "decision_date",
  "storage_period.name",
  "storage_period.bonus_in_yen",
  "grade_penalties",
  "adjustment_memo",
];

const getAdjustmentDealConverter = (translate: Translator): Exporter => {
  return async (data: any[]) => {
    let converted = data.concat([]);

    converted = translateValue(converted, "status", translate, "adjustment_deal_status");
    converted = translatePriceValuesByPriceCalculationMode(converted);
    converted = converted.map((target) => ({ ...target, id: `R${target.id}` }));

    converted = translateConfirmationTimeframe(converted, translate, "confirmation_timeframe");
    converted = translateColumn(converted, "confirmation_timeframe", null, "未確定");
    converted = sliceObjectColumn(converted, ["producer", "company_name"]);
    converted = sliceObjectColumn(converted, ["annum", "name"]);
    converted = sliceObjectColumn(converted, ["prefecture", "name"]);
    converted = sliceObjectColumn(converted, ["region", "name"]);
    converted = sliceObjectColumn(converted, ["brand", "name"]);
    converted = translateColumn(converted, "brand.name", "", "未確定");
    converted = sliceObjectColumn(converted, ["grade", "name"]);
    converted = translateColumn(converted, "grade.name", "", "未確定");
    converted = sliceObjectColumn(converted, ["packaging_form", "name"]);
    converted = translateColumn(converted, "packaging_form.name", "", "未確定");

    converted = translateColumn(converted, "trading_units", null, "未確定");
    converted = translateColumn(converted, "trading_sacks", null, "未確定");
    converted = translateColumn(converted, "price_calculation_mode", "", "未確定");
    converted = translateColumn(converted, "unit_price", null, "未確定");
    converted = translateColumn(converted, "trading_volume_in_yen", null, "未確定");

    converted = sliceObjectColumn(converted, ["storage_period", "name"]);
    converted = translateColumn(converted, "storage_period.name", "", "未確定");
    converted = sliceObjectColumn(converted, ["storage_period", "bonus_in_yen"]);
    converted = translateColumn(converted, "storage_period.bonus_in_yen", "", "未確定");
    converted = translateGradePenalties(converted);

    converted = sliceObjectArrayColumn(converted, "grade_penalties", ["name", "penalty_in_yen"], "grade_id");
    converted = sliceObjectArrayColumn(converted, "special_conditions", ["display_text"], "id");

    converted = formatDateColumns(converted, ["decision_date"]);

    converted = translateHeaderRow(converted, "adjustment_deals", translate, ADJUSTMENT_DEAL_COLUMNS);
    return unparse(converted);
  };
};

const translatePriceValuesByPriceCalculationMode = (targets: any[]) => {
  return targets.map((row) => {
    return {
      ...row,
      price_calculation_mode: formatPriceCalculationMode(row),
      unit_price: getDisplayUnitPriceValue(row),
      trading_volume_in_yen: getDisplayTradingVolumeValue(row),
      storage_period: {
        ...(row.storage_period || {}),
        bonus_in_yen: getDisplayStoragePeriodBonusValue(row.storage_period, row.price_calculation_mode),
      },
    };
  });
};

const OFFER_REQUEST_COLUMNS = [
  "updated_at",
  "annum_id",
  "updated_by_organization.prefecture_id",
  "region_id",
  "brand_id",
  "grade_id",
  "unit_price",
  "special_condition_ids",
];

const getOfferRequestConverter = (translate: Translator): Exporter => {
  return async (data: any[]) => {
    const [fetchedAnnums, fetchedPrefectures, fetchedRegions, fetchedBrands, fetchedGrades, fetchedSpecialConditions] =
      await Promise.all([
        getListAll("annums"),
        getListAll("prefectures"),
        getListAll("regions"),
        getListAll("brands"),
        getListAll("grades"),
        getListAll("special_conditions"),
      ]);
    const annums = fetchedAnnums.data;
    const prefectures = fetchedPrefectures.data;
    const regions = fetchedRegions.data;
    const brands = fetchedBrands.data;
    const grades = fetchedGrades.data;
    const specialConditions = fetchedSpecialConditions.data;

    let converted = data.concat([]);

    converted = formatDateColumns(converted, ["updated_at"]);

    converted = sliceObjectColumn(converted, ["updated_by_organization", "prefecture_id"]);

    converted = denormalizeId(converted, "annum_id", annums);
    converted = translateColumn(converted, "annum_id", "", "未設定");
    converted = denormalizeId(converted, "updated_by_organization.prefecture_id", prefectures);
    converted = denormalizeId(converted, "region_id", regions);
    converted = denormalizeId(converted, "brand_id", brands);
    converted = denormalizeId(converted, "grade_id", grades);
    converted = denormalizeIds(converted, "special_condition_ids", specialConditions, "display_text");

    converted = translateHeaderRow(converted, "offer_requests", translate, OFFER_REQUEST_COLUMNS);
    return unparse(converted);
  };
};
